import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import PlayerPendingDocumentsCard from "../components/PlayerPendingDocumentsCard";
import { api, ApiError } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import { paymentProviderLabel, publicPaymentStatusLabel } from "../lib/labels";

type OrderPaymentStatus = {
  order_id: number;
  status: string;
  provider: string | null;
  provider_reference: string | null;
  paid_at: string | null;
};

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 5;

export default function PublicOrderPaymentPage() {
  const { slug, orderToken } = useParams<{ slug: string; orderToken: string }>();
  const [searchParams] = useSearchParams();
  const resultado = searchParams.get("resultado");

  const [status, setStatus] = useState<OrderPaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutHidden, setCheckoutHidden] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState<string | null>(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [manualRefreshBusy, setManualRefreshBusy] = useState(false);
  const pollAttempts = useRef(0);

  const loadStatus = useCallback(async (initial: boolean) => {
    if (!orderToken) return;
    if (initial) setError(null);
    try {
      const response = await api.publicPayments.status(orderToken);
      setStatus(response.data);
    } catch (err) {
      // Los polls de fondo no deben tapar la última información conocida de
      // la orden; solo la carga inicial muestra el estado de error.
      if (initial) {
        setError(
          err instanceof ApiError
            ? err.message || "No pudimos consultar el estado de tu orden."
            : "Error de red. Intenta otra vez.",
        );
      }
    } finally {
      if (initial) setLoading(false);
    }
  }, [orderToken]);

  useEffect(() => {
    void loadStatus(true);
  }, [loadStatus]);

  // Polling suave: el webhook de Mercado Pago puede tardar unos segundos en
  // confirmar el pago cuando el jugador vuelve con ?resultado=exito.
  useEffect(() => {
    if (resultado !== "exito") return;
    if (!status || status.status === "paid") return;
    if (pollAttempts.current >= POLL_MAX_ATTEMPTS) {
      setPollExhausted(true);
      return;
    }

    const timer = setTimeout(() => {
      pollAttempts.current += 1;
      void loadStatus(false);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [resultado, status, loadStatus]);

  async function handleManualRefresh() {
    setManualRefreshBusy(true);
    try {
      await loadStatus(false);
    } finally {
      setManualRefreshBusy(false);
    }
  }

  async function handlePayWithMercadoPago() {
    if (!slug || !orderToken) return;
    setCheckoutBusy(true);
    setCheckoutNotice(null);
    setError(null);
    try {
      const response = await api.publicPayments.startCheckout(slug, orderToken, "mercadopago_marketplace");
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      setCheckoutNotice(response.data.message || "La orden ya no admite pago en línea.");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.status === 503)) {
        setCheckoutHidden(true);
        // El motivo exacto del backend (cuenta de cobro no activa, conexión
        // expirada, Mercado Pago sin configurar, orden de pareja…) importa
        // para que el organizador sepa qué arreglar; el genérico escondía todo
        // (Eduardo, 2026-09-04, torneo 59).
        const reason = err.message?.trim();
        setCheckoutNotice(
          reason
            ? `${reason} Mientras tanto puedes pagar por transferencia desde la página de inscripción.`
            : "El organizador aún no habilita pagos en línea; puedes pagar por transferencia desde la página de inscripción.",
        );
      } else {
        setError(
          err instanceof ApiError
            ? err.message || "No se pudo iniciar el pago con Mercado Pago."
            : "Error de red. Intenta otra vez.",
        );
      }
    } finally {
      setCheckoutBusy(false);
    }
  }

  const resultBanner = (() => {
    if (resultado === "exito") {
      if (status?.status === "paid") {
        return { kind: "success", text: "¡Pago recibido! Tu inscripción está confirmada." };
      }
      if (pollExhausted) {
        return { kind: "info", text: "El pago sigue en proceso. Usa el botón para actualizar el estado." };
      }
      return { kind: "info", text: "Mercado Pago está procesando tu pago; esta página se actualizará." };
    }
    if (resultado === "pendiente") {
      return {
        kind: "info",
        text: "Ficha generada. Tu lugar se confirma automáticamente cuando pagues (OXXO/SPEI pueden tardar).",
      };
    }
    if (resultado === "fallo") {
      return {
        kind: "warning",
        text: "El pago no se completó. Puedes intentar de nuevo o pagar por transferencia con comprobante.",
      };
    }
    return null;
  })();

  return (
    <section className="public-registration-page">
      <div className="card public-order-payment-card">
        <p className="eyebrow">Torneos123 · Pago de inscripción</p>
        <h1>Estado de tu orden</h1>

        {resultBanner ? (
          <div
            className={`alert alert--${resultBanner.kind === "warning" ? "error" : resultBanner.kind}`}
            role={resultBanner.kind === "warning" ? "alert" : "status"}
          >
            {resultBanner.text}
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Cargando estado de la orden…</p>
        ) : error ? (
          <div className="alert alert--error" role="alert">{error}</div>
        ) : status ? (
          <div className="public-order-status-panel">
            <div className="public-order-status-panel__icon" aria-hidden="true">$</div>
            <div>
              <span className="public-order-status-panel__eyebrow">Orden #{status.order_id}</span>
              <strong>{publicPaymentStatusLabel(status.status)}</strong>
              <div className="public-order-status-panel__details">
                {status.provider ? <small>Proveedor: {paymentProviderLabel(status.provider)}</small> : null}
                {status.paid_at ? <small>Pagado el {formatDateTime(status.paid_at)}</small> : null}
              </div>
            </div>
          </div>
        ) : null}

        {checkoutNotice ? <div className="alert alert--info" role="status">{checkoutNotice}</div> : null}

        {status?.status === "pending" && (status.provider === "manual" || checkoutHidden) && slug ? (
          // Cobro manual: el comprobante se sube en el paso 4 del link público,
          // no aquí (Eduardo 2026-09-12: la bienvenida traía a esta página y de
          // "Volver a la inscripción" caía en el paso 1).
          <div className="public-order-manual-box">
            <p>
              Este torneo cobra por transferencia. Haz tu pago y sube el comprobante en el link de
              inscripción para que el organizador lo valide.
            </p>
            <Link className="button button--primary" to={`/inscripcion/${slug}?paso=pago${orderToken ? `&orden=${encodeURIComponent(orderToken)}` : ""}`}>
              Subir comprobante de pago
            </Link>
          </div>
        ) : null}

        {status?.status === "pending" && status.provider !== "manual" && !checkoutHidden ? (
          <button
            className="button button--primary"
            type="button"
            onClick={handlePayWithMercadoPago}
            disabled={checkoutBusy}
          >
            {checkoutBusy ? "Redirigiendo…" : "Pagar con Mercado Pago"}
          </button>
        ) : null}

        {pollExhausted && status?.status !== "paid" ? (
          <button
            className="button button--secondary"
            type="button"
            onClick={handleManualRefresh}
            disabled={manualRefreshBusy}
          >
            {manualRefreshBusy ? "Actualizando…" : "Actualizar estado"}
          </button>
        ) : null}

        {slug && orderToken ? (
          // Documentos pendientes SIN cuenta (Eduardo 2026-10-29): quien aún
          // no tiene acceso llega aquí desde «Enviar documentos pendientes»
          // (con #documentos) y sube SOLO lo que falta; lo autoriza el token
          // de esta orden. No aparece si no falta nada o el torneo ya cerró.
          <div className="public-order-documents">
            <PlayerPendingDocumentsCard
              tournamentId={0}
              profileId={null}
              publicOrder={{ slug, orderToken }}
            />
          </div>
        ) : null}

        {slug ? (
          <p>
            <Link to={`/inscripcion/${slug}`}>Volver a la inscripción</Link>
          </p>
        ) : null}
      </div>
    </section>
  );
}
